import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Check, X, Trash2, Star, MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState('all'); // all, approved, pending

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/reviews`);
      setReviews(response.data);
    } catch (error) {
      toast.error('Failed to load reviews');
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const handleApprove = async (reviewId, currentStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${BACKEND_URL}/api/admin/reviews/${reviewId}`,
        { approved: !currentStatus },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      toast.success(currentStatus ? 'Review unapproved' : 'Review approved');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${BACKEND_URL}/api/admin/reviews/${reviewId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Review deleted successfully');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const filteredReviews = reviews.filter(review => {
    if (filter === 'approved') return review.approved;
    if (filter === 'pending') return !review.approved;
    return true;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-black text-white uppercase">Reviews Management</h2>
        
        <div className="flex gap-2">
          <Button
            onClick={() => setFilter('all')}
            variant={filter === 'all' ? 'default' : 'outline'}
            className={filter === 'all' ? 'bg-red-600 text-white' : 'border-zinc-700 text-gray-400'}
          >
            All ({reviews.length})
          </Button>
          <Button
            onClick={() => setFilter('approved')}
            variant={filter === 'approved' ? 'default' : 'outline'}
            className={filter === 'approved' ? 'bg-red-600 text-white' : 'border-zinc-700 text-gray-400'}
          >
            Approved ({reviews.filter(r => r.approved).length})
          </Button>
          <Button
            onClick={() => setFilter('pending')}
            variant={filter === 'pending' ? 'default' : 'outline'}
            className={filter === 'pending' ? 'bg-red-600 text-white' : 'border-zinc-700 text-gray-400'}
          >
            Pending ({reviews.filter(r => !r.approved).length})
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredReviews.map((review) => (
          <Card key={review.id} className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < review.rating ? 'fill-red-600 text-red-600' : 'text-gray-600'
                      }`}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  {review.approved ? (
                    <span className="px-2 py-1 bg-green-600/20 text-green-500 text-xs font-bold rounded uppercase">
                      Approved
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-yellow-600/20 text-yellow-500 text-xs font-bold rounded uppercase">
                      Pending
                    </span>
                  )}
                </div>
              </div>

              <p className="text-gray-300 leading-relaxed mb-4">
                "{review.comment}"
              </p>

              <div className="flex items-center justify-between pt-4 border-t border-zinc-800 mb-4">
                <div>
                  <p className="text-white font-bold">{review.name}</p>
                  <p className="text-red-500 text-sm uppercase tracking-wider">{review.service}</p>
                </div>
                <p className="text-gray-500 text-sm">
                  {new Date(review.date).toLocaleDateString()}
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => handleApprove(review.id, review.approved)}
                  className={`flex-1 ${
                    review.approved
                      ? 'bg-zinc-700 hover:bg-zinc-600'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {review.approved ? (
                    <><X className="w-4 h-4 mr-2" /> Unapprove</>
                  ) : (
                    <><Check className="w-4 h-4 mr-2" /> Approve</>
                  )}
                </Button>
                <Button
                  onClick={() => handleDelete(review.id)}
                  variant="destructive"
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredReviews.length === 0 && (
        <div className="text-center py-20">
          <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No reviews found</p>
        </div>
      )}
    </div>
  );
};

export default AdminReviews;
